import { ActionIcon, Button, Card, Group, Progress, Stack, Text, Title } from '@mantine/core'
import { IconPlayerPlay, IconTrash } from '@tabler/icons-react'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { CoverArt } from '~/components/blocks/CoverArt/CoverArt'
import type { QueueItem } from '~/components/player/types'
import { queueItemUid } from '~/components/player/utils'
import { useOfflineStore } from '~/hooks/use-offline-store'
import { usePlayer } from '~/hooks/use-player'
import { formatDuration, formatFileSize } from '~/lib/format'
import { type DownloadRecord, getUsage, listDownloads } from '~/lib/offline/downloads'

export const Route = createFileRoute('/_app/downloads/')({
  component: DownloadsPage,
})

function recordToQueueItem(record: DownloadRecord): QueueItem {
  const { meta } = record
  return {
    uid: queueItemUid(),
    id: meta.id,
    title: meta.title,
    artist: meta.artist,
    artistId: meta.artistId,
    album: meta.album,
    albumId: meta.albumId,
    duration: meta.duration,
    coverArt: meta.coverArt,
    replayGain: null,
    tempo: null,
    keyName: null,
    energy: null,
    mood: null,
  }
}

function DownloadsPage() {
  const player = usePlayer()
  const downloadedIds = useOfflineStore((s) => s.downloadedIds)
  const remove = useOfflineStore((s) => s.remove)
  const clearAll = useOfflineStore((s) => s.clearAll)

  // Re-query IndexedDB whenever the set of downloaded tracks changes.
  const idKey = [...downloadedIds].sort((a, b) => a - b)
  const { data: records = [] } = useQuery({
    queryKey: ['offline-downloads', idKey],
    queryFn: listDownloads,
  })
  const { data: usage } = useQuery({ queryKey: ['offline-usage', idKey], queryFn: getUsage })

  // Group downloads by album, preserving disc/track order via title fallback.
  const albums = new Map<number, DownloadRecord[]>()
  for (const r of records) {
    const list = albums.get(r.meta.albumId) ?? []
    list.push(r)
    albums.set(r.meta.albumId, list)
  }

  const playFrom = (albumRecords: DownloadRecord[], index: number) => {
    player.play(albumRecords.map(recordToQueueItem), { startIndex: index })
  }

  if (records.length === 0) {
    return (
      <Stack gap="md">
        <Title order={2}>Downloads</Title>
        <Text c="dimmed">
          No downloaded music yet. Use the download button on an album, playlist, or track to make
          it available offline.
        </Text>
      </Stack>
    )
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>Downloads</Title>
        <Button
          variant="subtle"
          color="red"
          leftSection={<IconTrash size={16} />}
          onClick={() => clearAll()}
        >
          Remove all
        </Button>
      </Group>

      {usage && usage.quota > 0 && (
        <Stack gap={4}>
          <Text size="sm" c="dimmed">
            {formatFileSize(usage.usage)} used of {formatFileSize(usage.quota)}
          </Text>
          <Progress value={(usage.usage / usage.quota) * 100} />
        </Stack>
      )}

      {[...albums.values()].map((albumRecords) => {
        const first = albumRecords[0]!
        return (
          <Card key={first.meta.albumId} withBorder padding="md">
            <Group align="flex-start" gap="md" wrap="nowrap">
              <CoverArt
                type="albums"
                id={first.meta.albumId}
                alt={first.meta.album}
                size={300}
                w={80}
                h={80}
              />
              <Stack gap={4} style={{ flex: 1 }}>
                <Text fw={600}>{first.meta.album}</Text>
                <Text size="sm" c="dimmed">
                  {first.meta.artist} · {albumRecords.length} tracks
                </Text>
                <Stack gap={2} mt="xs">
                  {albumRecords.map((r, i) => (
                    <Group key={r.trackId} justify="space-between" wrap="nowrap">
                      <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
                        <ActionIcon
                          variant="subtle"
                          size="sm"
                          onClick={() => playFrom(albumRecords, i)}
                          aria-label="Play"
                        >
                          <IconPlayerPlay size={14} />
                        </ActionIcon>
                        <Text size="sm" truncate>
                          {r.meta.title}
                        </Text>
                      </Group>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="xs" c="dimmed">
                          {formatDuration(r.meta.duration)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {formatFileSize(r.bytes)}
                        </Text>
                        <ActionIcon
                          variant="subtle"
                          color="red"
                          size="sm"
                          onClick={() => remove(r.trackId)}
                          aria-label="Remove download"
                        >
                          <IconTrash size={14} />
                        </ActionIcon>
                      </Group>
                    </Group>
                  ))}
                </Stack>
              </Stack>
            </Group>
          </Card>
        )
      })}
    </Stack>
  )
}
